/*
  Custom Autoship control for cart line items (drawer + cart page). Replaces
  Appstle's own injected cart widget (which was path-gated to /cart and couldn't be
  styled to match the drawer). The markup is in snippets/cart-items.liquid; this
  script turns the One-time / Autoship radios + frequency <select> into a
  /cart/change call that sets the line's `selling_plan` (an Appstle native selling
  plan), then re-renders the cart using the theme's OWN <cart-items> pipeline
  (getSectionsToRender + getElementHTML) so line prices, totals and the widget's own
  state all refresh exactly like a quantity change does.
*/
(function () {
  /* Writes a money string with Amazon-style cents, matching the Liquid-rendered prices around it.
     Falls back to plain text if llm-money.js is absent, so a missing script means flat cents rather
     than tags rendered as visible characters. */
  function setMoneyHtml(el, s) {
    if (window.llmMoney) el.innerHTML = window.llmMoney.cents(s || '');
    else el.textContent = s || '';
  }

  // The selling_plan id to apply for a widget's current UI state (null = one-time).
  function planFor(widget) {
    var autoship = widget.querySelector('input[value="autoship"]');
    if (!autoship || !autoship.checked) return null;
    var sel = widget.querySelector('.llm-autoship__freq');
    return sel && sel.value ? sel.value : null;
  }

  var busy = false;

  async function applyChange(widget) {
    var cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;
    if (busy) return;
    busy = true;
    widget.classList.add('llm-autoship--loading');

    var line = Number(widget.dataset.line);
    var plan = planFor(widget); // string id or null

    var toRender = cartItems.getSectionsToRender();
    var sections = [];
    toRender.forEach(function (s) {
      if (sections.indexOf(s.section) === -1) sections.push(s.section);
    });

    var url = (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';

    try {
      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line,
          selling_plan: plan,
          sections: sections,
          sections_url: window.location.pathname
        })
      });
      var data = await res.json();
      if (!res.ok) throw new Error((data && data.message) || 'cart change failed');

      // Re-render the same sections the theme would after a quantity change.
      toRender.forEach(function (s) {
        var sectionEl = document.getElementById(s.id);
        if (!sectionEl) return;
        var el = sectionEl.querySelector(s.selector) || sectionEl;
        var html = data.sections && data.sections[s.section];
        if (html != null) el.innerHTML = cartItems.constructor.getElementHTML(html, s.selector);
      });

      // Let any cart listeners (counts/upsells) know the cart changed.
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    } catch (e) {
      console.error('[llm-autoship]', e);
      widget.classList.remove('llm-autoship--loading');
    } finally {
      busy = false;
    }
  }

  /* ---- Quick-view drawer: reflect the chosen Autoship price in the drawer -------
     There's no cart line yet, so instead of /cart/change we just repaint the theme's
     own price element. Data comes from the <script class="llm-qa-plandata"> map that
     Liquid embeds in the widget: { variantId: { onetime, <planId>: <price>, ... } }. */
  function qaData(widget) {
    var el = widget.querySelector('.llm-qa-plandata');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function qaVariantId() {
    var form = document.getElementById('product-form-quickadd');
    var input = form && form.querySelector('[name="id"]');
    return input ? input.value : null;
  }
  function renderQaPrice(widget) {
    var data = qaData(widget);
    if (!data) return;
    var vmap = data[qaVariantId()];
    if (!vmap) return;
    var drawer =
      widget.closest('quick-add-drawer') ||
      widget.closest('[class*="quick-add-drawer"]') ||
      document;
    var autoship = widget.querySelector('input[value="autoship"]');
    var freq = widget.querySelector('.llm-autoship__freq');
    var on = !!(autoship && autoship.checked);
    var sub = on && freq && freq.value ? vmap[freq.value] : null;

    drawer.querySelectorAll('.price').forEach(function (p) {
      var cur = p.querySelector('.price__current .js-value');
      if (!cur) return;
      var wasWrap = p.querySelector('.price__was');
      var wasVal = wasWrap && wasWrap.querySelector('.js-value');
      var wasLabel = wasWrap && wasWrap.querySelector('.js-label');
      if (on && sub) {
        // Stash the untouched (variant-picker-set) values once, so One-time restores cleanly.
        /* innerHTML, not textContent, on both the stash and the write (owner 2026-09-04). The
           price Liquid rendered now contains a <sup> around the cents; reading textContent threw
           that markup away, so switching to Autoship and back left a flat price sitting next to
           superscripted ones. The plan prices in llm-qa-plandata are still flat strings — they go
           through | json, where markup has no business — so they get the cents treatment here at
           render time instead. */
        if (!p.classList.contains('llm-qa-priced')) {
          cur.dataset.llmOrig = cur.innerHTML;
          if (wasVal) wasVal.dataset.llmOrig = wasVal.innerHTML;
          if (wasLabel) wasLabel.dataset.llmOrig = wasLabel.textContent;
          p.dataset.llmOrigSale = p.classList.contains('price--on-sale') ? '1' : '0';
        }
        setMoneyHtml(cur, sub);
        p.classList.add('price--on-sale', 'llm-qa-priced');
        if (wasVal) setMoneyHtml(wasVal, vmap.onetime);
        if (wasLabel) wasLabel.textContent = 'One-time price';
      } else if (p.classList.contains('llm-qa-priced')) {
        if (cur.dataset.llmOrig != null) cur.innerHTML = cur.dataset.llmOrig;
        if (wasVal) wasVal.innerHTML = wasVal.dataset.llmOrig || '';
        if (wasLabel) wasLabel.textContent = wasLabel.dataset.llmOrig || '';
        if (p.dataset.llmOrigSale === '0') p.classList.remove('price--on-sale');
        p.classList.remove('llm-qa-priced');
      }
    });
  }

  /* ---- PDP buy box: keep both option-card prices honest ------------------------
     The PDP picker (scope 'pdp') prints BOTH prices at once — one-time in its card, Autoship in
     its card — so unlike the drawer there is nothing to swap on selection: the comparison is the
     point. Only a VARIANT change invalidates them.

     This is why there is no main-price driver here. The audit flagged "the displayed price never
     changes" as a gap on the assumption we would repaint .product-info__price when Autoship is
     picked; showing both prices in the cards answers it without a second writer fighting
     variant-picker.js over the same node.

     Replaces the per-card price rendering Appstle did itself, and with it llm-money-appstle.js —
     which existed only because that price was app-rendered and out of reach of the theme's
     formatter. setMoneyHtml gives us the superscript cents natively. */
  function pdpVariantId(widget) {
    // Via the frequency select's form= association, so this never hard-codes a section id.
    var freq = widget.querySelector('.llm-autoship__freq');
    var formId = freq && freq.getAttribute('form');
    var form = formId && document.getElementById(formId);
    var input = form && form.querySelector('[name="id"]');
    if (input) return input.value;
    var fallback = document.querySelector('.llm-buybox-sidebar [name="id"]');
    return fallback ? fallback.value : null;
  }
  function renderPdpPrices(widget) {
    var el = widget.querySelector('.llm-pdp-plandata');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    var vmap = data && data[pdpVariantId(widget)];
    if (!vmap) return;

    var one = widget.querySelector('[data-llm-price="onetime"]');
    if (one && vmap.onetime) setMoneyHtml(one, vmap.onetime);

    var freq = widget.querySelector('.llm-autoship__freq');
    var planPrice = freq && freq.value ? vmap[freq.value] : null;
    if (!planPrice) {
      // No frequency chosen yet (the select is disabled until Autoship is picked) — fall back to
      // any plan key, since every frequency carries the same percentage.
      for (var k in vmap) { if (k !== 'onetime') { planPrice = vmap[k]; break; } }
    }
    var auto = widget.querySelector('[data-llm-price="autoship"]');
    if (auto && planPrice) setMoneyHtml(auto, planPrice);

    /* "/ea" beside each card price. Liquid printed it for the variant the page LOADED on and nothing
       refreshed it, so after picking another size both cards kept the first variant's figure
       (owner 2026-10-26: "it's not changing in either box" — MEGAMAX showed $15.99 next to $3.75/ea,
       which is $14.99 / 4). Recomputed from the price now shown and the variant's unit count ("q" in
       the plandata), cents rounded exactly as the Liquid does; hidden when the variant has none. */
    var count = llmCountFor(pdpVariantId(widget)) || vmap.q;
    setCardUnit(one, vmap.onetime, count);
    setCardUnit(auto, planPrice, count);
  }
  /* Units in a variant, from the SAME map the option tiles use (#llm-variant-value-data, counted
     from the variant label — see snippets/llm-variant-value-data.liquid for why the label beats
     Shopify's quantity_value). 0 when unknown, and the caller falls back to quantity_value. */
  function llmCountFor(vid) {
    try {
      var el = document.getElementById('llm-variant-value-data');
      var m = el ? JSON.parse(el.textContent) : null;
      var v = m && vid ? m[vid] : null;
      if (v && v.count > 1) return Number(v.count);
    } catch (e) {}
    return llmCountFromTitle(llmSelectedTitle());
  }
  /* Units from the variant NAME, the same first-" of " rule llm-variant-value-data.liquid uses:
     "Trial Pack of 4 *MADE IN USA*" -> 4, "Case of 2,000 (20 boxes of 100)" -> 2000, "100 ct" -> 100.
     Needed because that map deliberately leaves samples/trials OUT (so they can never win Best
     value), and on MEGAMAX's Purple/Pink/Black/Tie-Dye trials Shopify has no unit price either. */
  function llmCountFromTitle(title) {
    var t = String(title || '');
    var m = t.match(/\bof\s+([\d,]+)/i) || t.match(/([\d,]+)\s*(?:ct|count)\b/i);
    var n = m ? Number(m[1].replace(/,/g, '')) : 0;
    return n > 1 ? n : 0;
  }
  function llmSelectedTitle() {
    var o = document.querySelector('.product-info .custom-select__option[aria-selected="true"], .cc-main-product .custom-select__option[aria-selected="true"]');
    return o ? (o.getAttribute('data-value') || o.textContent) : '';
  }
  function setCardUnit(priceEl, priceStr, q) {
    if (!priceEl) return;
    var row = priceEl.parentElement;
    var unit = row && row.querySelector('.llm-autoship__card-unit');
    var cents = Math.round(parseFloat(String(priceStr || '').replace(/[^0-9.]/g, '')) * 100);
    if (!(q > 0) || !(cents > 0)) { if (unit) unit.hidden = true; return; }
    if (!unit) {
      unit = document.createElement('span');
      unit.className = 'llm-autoship__card-unit';
      priceEl.insertAdjacentElement('afterend', unit);
    }
    unit.hidden = false;
    unit.textContent = '$' + (Math.round(cents / q) / 100).toFixed(2) + '/ea';
  }

  /* ---- PDP: the WHOLE card is a click target -----------------------------------
     Only the <label> head was clickable, so clicking the price, the benefits or the empty space did
     nothing (owner, 2026-10-12). The body is deliberately NOT inside the label — if it were, opening
     the frequency <select> would toggle the radio — so the rest of the card is wired up here instead.

     ⛔ IGNORE CLICKS ON ANYTHING INTERACTIVE. The card contains a real <select>, and the delivery
     line carries an (i) that opens a ZIP popup (llm-shipping-eta.js) plus links. Selecting the
     option out from under someone who was reaching for one of those is worse than the dead space
     this fixes. The label itself is excluded too: it already works natively, and handling it here as
     well would fire change twice. */
  var INTERACTIVE = 'a,button,select,input,textarea,label,summary,[role="button"],.info-ship,.popup-box-message';

  document.addEventListener('click', function (e) {
    var card = e.target.closest && e.target.closest('.llm-autoship--pdp .llm-autoship__card');
    if (!card) return;
    if (e.target.closest(INTERACTIVE)) return;

    var radio = card.querySelector('input[type="radio"]');
    if (!radio || radio.checked) return;        // already selected — nothing to do, no event to fire
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Per-option selected class. The active-card CSS gate
     (llm-product-highlights-v2.css:577-588) hides stock / delivery / footnote in the card that is
     NOT chosen — without this they show in both cards at once. Appstle supplied this via
     .appstle-active-option, which llm-autoship-toggle-v2.js:286-304 had to keep un-stale because
     Appstle left the class on deselected cards. Driven off :checked here, so it cannot go stale. */
  /* ⚠️ MUST stay value-scoped, not a bare input[type="radio"]. Appstle's hidden PDP widget injects
     its own `selling_plan` / `selling_plan_radio` inputs INSIDE our one-time card — one of them is
     always :checked, so a bare radio selector reads Appstle's state instead of ours and reports the
     wrong card as selected. Appstle's values are plan IDs or "", so matching on our two values is
     unambiguous. Measured on the preview 2026-10-12. */
  var OPTION_RADIOS = 'input[type="radio"][value="onetime"], input[type="radio"][value="autoship"]';

  /* ⚠️ Spelled out rather than OPTION_RADIOS + ':checked'. A pseudo-class appended to a
     comma-separated selector list binds to the LAST clause only, so the concatenated form matches
     any one-time radio whether checked or not — it silently always reported one-time as selected. */
  var OPTION_RADIO_CHECKED =
    'input[type="radio"][value="onetime"]:checked, input[type="radio"][value="autoship"]:checked';

  function syncCardState(widget) {
    widget.querySelectorAll('.llm-autoship__card').forEach(function (card) {
      var radio = card.querySelector(OPTION_RADIOS);
      card.classList.toggle('is-on', !!(radio && radio.checked));
    });
  }

  // Re-apply prices after a variant change (the picker replaces the price node).
  document.addEventListener('on:variant:change', function () {
    var qa = document.querySelector('.llm-autoship--qa');
    if (qa) setTimeout(function () { renderQaPrice(qa); }, 0);
    var pdp = document.querySelector('.llm-autoship--pdp');
    if (pdp) setTimeout(function () { renderPdpPrices(pdp); }, 0);
  });

  // First paint: Liquid already rendered the selected variant's prices, so this only matters when
  // something changed the variant before this script ran.
  document.addEventListener('DOMContentLoaded', function () {
    var pdp = document.querySelector('.llm-autoship--pdp');
    if (pdp) { renderPdpPrices(pdp); syncCardState(pdp); }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var widget = t.closest('.llm-autoship');
    if (!widget) return;
    var isRadio = t.matches('input[type="radio"]');
    var isFreq = t.classList && t.classList.contains('llm-autoship__freq');
    if (!isRadio && !isFreq) return;

    // Immediate UI feedback: enable/disable the frequency select + toggle class.
    var autoship = widget.querySelector('input[value="autoship"]');
    var freq = widget.querySelector('.llm-autoship__freq');
    var on = !!(autoship && autoship.checked);
    if (freq) freq.disabled = !on;
    widget.classList.toggle('is-autoship', on);
    // (The "dropdown missing on product-page quick-buy" bug was Appstle's CSS positioning our
    // name="selling_plan" select absolutely — fixed in llm-autoship.css, not here.)

    // Quick-view drawer: pre-purchase selector. The <select name="selling_plan"
    // form="product-form-quickadd"> is submitted with add-to-cart only while enabled,
    // so toggling `disabled` above is all that's needed — no cart change to apply.
    // Also repaint the drawer's price to reflect the chosen option.
    if (widget.classList.contains('llm-autoship--qa')) {
      renderQaPrice(widget);
      return;
    }

    /* PDP buy box. The <select name="selling_plan" form="product-form-<section>"> is submitted with
       add-to-cart only while enabled, so the `disabled` toggle above is the whole one-time path —
       nothing else to apply. Card prices do not depend on the selection (both are shown), but the
       chosen FREQUENCY can change which plan price is displayed, and the card state drives the
       active-card CSS gate. */
    if (widget.classList.contains('llm-autoship--pdp')) {
      syncCardState(widget);
      renderPdpPrices(widget);
      return;
    }

    // Cart line: only act on a real line item (avoids a bogus /cart/change).
    if (!widget.dataset.line) return;
    applyChange(widget);
  });
})();
